/* ============================================================
   glow-instagram-wall.js — <glow-instagram-wall> host element.
   עוטף <glow-loop-carousel> פנימי (grid בדסקטופ / swipe במובייל).
   אחראי על: כפתור החשיפה ("Show All" — חושף את *כל* האריחים בבת אחת
   ותוחם את גובה הקיר לתקרה), ועל הזרקת הפוסטים החיים מאינסטגרם
   (Instafeed v6).
   ה-stagger וה-masonry הם CSS טהור (nth-child + grid-row span) —
   כאן רק לוגיקת החשיפה וההזרקה.

   כללי ההזרקה (הכרעת עילי):
   1. פוסט חי נכנס בראש הרשימה — ימין-למעלה ב-RTL, לפני המצוירים.
   2. 12 האריחים המצוירים לא נמחקים לעולם; הם נדחפים מתחת לקו ה-12
      ונשארים זמינים ב"ראה עוד".
   3. בדסקטופ תמיד בדיוק initial_count גלויים (N חיים + השלמה מצוירים).
   4. במובייל הקרוסלה המעגלית מציגה הכל, בדיוק כמו היום.
   5. כל כשל (אין אפליקציה / אין פוסטים / רשת) → no-op שקט מוחלט:
      הקיר נשאר על האריחים המצוירים ונראה בדיוק כמו בלי הפיד.

   ── ארכיטקטורת הפיד (שוכתבה 2026-08-02) ──────────────────────
   קודם קראנו ישירות ל-API של Instafeed. זה נשבר ב-401 ברגע שהם
   הוסיפו פרמטר hash לאימות — API לא-מתועד שלא הבטיחו לנו כלום לגביו.
   עכשיו אנחנו לא מדברים איתם בכלל: הסקריפט *שלהם* (נטען ע"י ה-app
   embed מה-CDN שלהם, ומתעדכן לבד) עושה את כל העבודה — אימות, hash,
   גרסאות, שינויים עתידיים — ואנחנו רק אוספים את התוצאה.

   שתי דרכי-איסוף, לפי סדר עדיפות, שתיהן פסיביות:
   A. capture — עוטפים את window.fetch ומקשיבים לתשובת הפיד שהסקריפט
      שלהם ממילא מושך. נותן את אובייקטי הפוסט המלאים (permalink, קפשן).
      העטיפה שקופה, מסירה את עצמה, ולא נוגעת בבקשות של אף אחד אחר.
   B. DOM — קוראים את האריחים שהם רינדרו למכולה המוסתרת
      (.instafeed-new-layout-item[data-instafeed-thumbnail]). גיבוי למקרה
      שיעברו מ-fetch ל-XHR. אין שם permalink פר-פוסט (openIg=3), ולכן
      הקישור נופל לפרופיל.

   מדידה אמפירית (סטייג'ינג, 2026-08-02) — שני כללים שאסור לשבור:
   • ה-CSS שלהם דורס display על #insta-feed → ההסתרה חיה על העוטף שלנו.
   • run() נוטש ל-ResizeObserver כש-clientWidth==0 → מכולה ברוחב אפס
     לא תרנדר לעולם. הרוחב נקבע ב-CSS (.glow-igw-source > #insta-feed).

   זהירות (הלקח מ-arc-carousel): לא נוגעים ב-this.dir/id/title/hidden
   כ-state — התנגשות DOM שקטה ששוברת web components. guard כפול.
   ============================================================ */
const GLOW_IG_SOURCE_ID = 'insta-feed';
const GLOW_IG_FEED_URL_RE = /nfcube\.com\/feed\//i;
// כמה זמן ממתינים לסקריפט שלהם לפני שמכריזים "לא נטען" ונשארים סטטיים.
const GLOW_IG_SOURCE_TIMEOUT = 12000;
// עוטף ה-fetch מסיר את עצמו גם אם שום בקשת פיד לא הגיעה.
const GLOW_IG_CAPTURE_TTL = 45000;
// 04.10.2026 · מדידת ממדי התמונות לפני ההזרקה (GLOW_IG_PROBE_TIMEOUT) ירדה:
// האריח מקבל את הגובה שלו מ-aspect-ratio ב-CSS (3/4, ובדף הבית במובייל 3/4
// בגריד), ו-span/--aspect-ratio כבר לא משפיעים על הפריסה. המדידה הורידה את כל
// 16 התמונות במלואן, גם כשהקיר מחוץ למסך (רשת הביטחון של 8 שניות במאמר).
// הפיד נמשך רק כשהקיר מתקרב לוויוport — לא נאבקים על רוחב-פס עם ה-LCP.
const GLOW_IG_NEAR_VIEWPORT = '900px';
// רשת ביטחון: מתחילים בכל מקרה אחרי הזמן הזה, גם אם ה-IntersectionObserver
// לא ירה. מאוחר מספיק כדי לא לגעת ב-LCP, ומונע 'pending' נצחי.
const GLOW_IG_FALLBACK_START = 8000;
const GLOW_IG_DOM_ALT_PREFIX = /^Instagram post with the caption:\s*/i;
const GLOW_IG_SIZES = '(min-width: 990px) 24vw, (min-width: 750px) 30vw, 45vw';
const GLOW_IG_ALT_FALLBACK = 'פוסט באינסטגרם';
// 24.09.2026 — GLOW_IG_EXPANDED_CAP / GLOW_IG_DESKTOP_MQ / GLOW_IG_SCROLL_LABEL_FALLBACK
// ירדו יחד עם תקרת-הגובה שאחרי "Show All" (ראה #releaseCap).

/* ------------------------------------------------------------
   שכבת ה-capture: מאזין פסיבי לתשובת הפיד של Instafeed.
   סינגלטון ברמת הדף — גם אם יש שני קירות בעמוד, עוטפים fetch פעם אחת.
   העטיפה מחזירה את ה-Promise המקורי כמות שהוא ומשתמשת ב-clone() לקריאה,
   כך שאף צרכן אחר לא מושפע. היא מסירה את עצמה ברגע שהתקבלה תשובה
   (או אחרי TTL) — ורק אם היא עדיין העטיפה הפעילה, כדי לא לדרוס עטיפה
   של סקריפט אחר שנכנס אחרינו.
   ------------------------------------------------------------ */
class GlowIgFeedCapture {
  #installed = false;
  #original = null;
  #patched = null;
  #result = null;
  #listeners = [];
  #ttlTimer = 0;

  install() {
    if (this.#installed || typeof window.fetch !== 'function') return;
    this.#installed = true;
    this.#original = window.fetch;

    this.#patched = (...args) => {
      const promise = this.#original.apply(window, args);
      if (!GLOW_IG_FEED_URL_RE.test(this.#urlOf(args[0]))) return promise;

      promise.then(
        (response) => {
          if (!response || !response.ok) {
            this.#settle(null, `http-${response ? response.status : 'error'}`);
            return;
          }
          response
            .clone()
            .json()
            .then((payload) => {
              const posts = Array.isArray(payload?.data) ? payload.data : null;
              if (posts) this.#settle(posts, null);
              else this.#settle(null, `meta-${payload?.meta?.code ?? 'unknown'}`);
            })
            .catch(() => this.#settle(null, 'parse-error'));
        },
        () => this.#settle(null, 'network-error')
      );

      return promise;
    };

    window.fetch = this.#patched;
    this.#ttlTimer = setTimeout(() => this.#uninstall(), GLOW_IG_CAPTURE_TTL);
  }

  // מחזיר את התוצאה אם כבר הגיעה, ואחרת רושם מאזין חד-פעמי.
  subscribe(callback) {
    if (this.#result) {
      callback(this.#result);
      return;
    }
    this.#listeners.push(callback);
  }

  // string | Request (.url) | URL (.href). בלי instanceof — הוא תלוי בזהות
  // הקונסטרקטור ונשבר בין realms (iframe/סנדבוקס), וכאן זה נתיב קריטי.
  #urlOf(input) {
    try {
      if (typeof input === 'string') return input;
      if (typeof input?.url === 'string') return input.url;
      if (typeof input?.href === 'string') return input.href;
      return '';
    } catch (error) {
      return '';
    }
  }

  #settle(posts, error) {
    if (this.#result) return;
    this.#result = { posts, error };
    this.#uninstall();
    const listeners = this.#listeners;
    this.#listeners = [];
    for (const listener of listeners) listener(this.#result);
  }

  #uninstall() {
    clearTimeout(this.#ttlTimer);
    if (!this.#installed) return;
    this.#installed = false;
    if (window.fetch === this.#patched) window.fetch = this.#original;
  }
}

const glowIgFeedCapture = new GlowIgFeedCapture();

/* מתקינים את ה-capture ברגע שהסקריפט הזה נטען — הנקודה המוקדמת ביותר
   שקיימת לנו, לפני שדרוג האלמנט ולפני connectedCallback. הקובץ נטען ב-defer
   (כלומר תמיד לפני DOMContentLoaded), בעוד הסקריפט של Instafeed נטען async
   ומבצע את ה-fetch רק אחריו — נמדד בפרודקשן 2026-08-02: הפיד שלהם מרונדר
   ~200ms אחרי DCL. ההתקנה כאן מגנה גם על המקרה שבו שדרוג ה-custom element
   מתעכב (long task, עומס CPU במובייל).
   למה זה קריטי: ה-permalink האמיתי קיים אך ורק בתשובת ה-fetch. נבדק בפרודקשן
   שה-DOM שהם מרנדרים הוא <div> עם clip-path — בלי <a href>, בלי <img>, ובלי
   permalink בשום data-attribute — וגם window.InstafeedAPI לא מחזיק את הפוסטים.
   לכן החמצה של ה-capture = ok-dom וקישור לפרופיל במקום לפוסט. */
if (typeof document !== 'undefined') {
  const glowIgLiveWallPresent =
    document.readyState === 'loading' ||
    Boolean(document.querySelector('glow-instagram-wall[data-igw-live="true"]'));
  if (glowIgLiveWallPresent) glowIgFeedCapture.install();
}

class GlowInstagramWall extends HTMLElement {
  #nearObserver = null;
  #screenObserver = null;
  #feedObserver = null;
  #feedTimer = 0;
  #fallbackTimer = 0;
  #feedStarted = false;
  #destroyed = false;
  // מונה ריצות: כל חיבור/ניתוק מקדם אותו. ריצת-פיד שהתחילה בדור קודם
  // (למשל לפני re-render של הסקשן) מזהה זאת כשהיא חוזרת מה-await ופורשת,
  // כדי שלא תזריק פעמיים לצד הריצה החדשה.
  #generation = 0;
  // משחרר המתנה תלויה-באוויר בעת ניתוק, כדי שה-await לא יישאר לנצח.
  #abortWait = null;

  connectedCallback() {
    this.grid = this.querySelector('[data-igw-grid]');
    this.moreWrap = this.querySelector('[data-igw-more-wrap]');
    this.moreBtn = this.querySelector('[data-igw-more]');
    if (!this.grid) return;

    this.#destroyed = false;
    this.#generation += 1;
    this.initial = parseInt(this.dataset.igwInitial, 10) || 12;
    this.liveEnabled = this.dataset.igwLive === 'true';
    this.liveTemplate = this.querySelector('[data-igw-live-template]');

    // 24.09.2026 — מאזיני matchMedia/resize ירדו: הם שירתו אך ורק את מדידת
    // תקרת-הגובה שאחרי "Show All", והתקרה הוסרה. הגריד נפרש עכשיו לפי
    // התוכן, ולכן אין מה למדוד מחדש בשינוי רוחב.

    this.#initSeeMore();
    this.#initScreenWatch();
    this.#initFeedHook();
  }

  // 04.10.2026 · data-igw-onscreen כשהקיר בתוך המסך. ה-CSS מריץ את ה-crossfade
  // האינסופי (מגע בלבד) רק כשהסימן קיים, ולכן מחוץ למסך אין אנימציות פעילות.
  // בלי IntersectionObserver מסמנים מיד, כלומר ההתנהגות של היום.
  #initScreenWatch() {
    this.#screenObserver?.disconnect();
    if (!('IntersectionObserver' in window)) {
      this.setAttribute('data-igw-onscreen', '');
      return;
    }
    this.#screenObserver = new IntersectionObserver((entries) => {
      const visible = entries.some((entry) => entry.isIntersecting);
      this.toggleAttribute('data-igw-onscreen', visible);
    });
    this.#screenObserver.observe(this);
  }

  disconnectedCallback() {
    this.#destroyed = true;
    this.#generation += 1;
    // הרכיב עלול לחזור (re-render של סקשן, העברת צומת). משחררים את הדגל
    // ואת ההמתנה התלויה, אחרת החיבור הבא יפרוש מיד והקיר ייתקע ב-'pending'
    // לצמיתות — כשל שקט שנראה בדיוק כמו פיד שנכשל.
    this.#feedStarted = false;
    this.#abortWait?.();
    if (this.onMore) this.moreBtn?.removeEventListener('click', this.onMore);
    clearTimeout(this.#feedTimer);
    clearTimeout(this.#fallbackTimer);
    this.#nearObserver?.disconnect();
    this.#nearObserver = null;
    this.#screenObserver?.disconnect();
    this.#screenObserver = null;
    this.#stopFeedObserver();
  }

  /* ---------------- "Show All" ---------------- */

  // חושף בלחיצה אחת את *כל* האריחים החבויים (מעל initial_count) ומסתיר את
  // הכפתור. הגריד בדסקטופ מציג את החשופים דרך .is-expanded; במובייל
  // הקרוסלה מציגה הכל ממילא.
  // אידמפוטנטי: נקרא שוב אחרי הזרקת פוסטים חיים, ולכן מנתק מאזין קודם.
  #initSeeMore() {
    if (!this.moreBtn || !this.moreWrap) return;

    if (this.onMore) this.moreBtn.removeEventListener('click', this.onMore);
    this.onMore = null;

    if (this.#hiddenTiles().length === 0) {
      this.moreWrap.hidden = true;
      return;
    }

    this.moreWrap.hidden = false;
    this.moreBtn.setAttribute('aria-expanded', 'false');
    this.onMore = this.#revealAll.bind(this);
    this.moreBtn.addEventListener('click', this.onMore);
  }

  // מתעלם מ-clones של הקרוסלה (מובייל) — הם עותקים, לא פריטים אמיתיים.
  #hiddenTiles() {
    return Array.from(
      this.grid.querySelectorAll('[data-igw-hidden]:not([data-glow-loop-clone])')
    );
  }

  #realTiles() {
    return Array.from(
      this.grid.querySelectorAll('[data-glow-loop-item]:not([data-glow-loop-clone])')
    );
  }

  #revealAll() {
    this.classList.add('is-expanded');

    for (const tile of this.#hiddenTiles()) {
      tile.removeAttribute('data-igw-hidden');
      tile.classList.remove('glow-ig-tile--hidden');
    }

    this.moreWrap.hidden = true;
    this.moreBtn.setAttribute('aria-expanded', 'true');
    this.#releaseCap();
  }

  /* ---------------- שחרור תקרת-הגובה ---------------- */

  // 24.09.2026 — **התקרה הוסרה** (אימות בפרודקשן + אישור עילי).
  // עד היום: אחרי "Show All" הגריד קיבל max-height: --igw-cap-height ו-
  // overflow-y: auto, כלומר כל 28 האריחים היו "גלויים" אבל חלקם נחתך מתחת
  // לסקרולבר פנימי. נמדד חי בפרודקשן 24.09: ב-1440px התקרה 1048px מול
  // scrollHeight 1735px (687px מוסתרים); ברוחב אחר 771px מול 1273px.
  // הדרישה החדשה: Show All פורש את הקיר במלואו — בלי תקרה ובלי גלילה פנימית.
  // לכן #syncCap/#measureCap ירדו, ונשאר רק הניקוי: הוא נקרא פעם אחת ב-
  // #revealAll כדי שלא יישארו שאריות (is-scroll-capped / --igw-cap-height /
  // tabindex+role+aria-label) אם דפדפן טעון-קאש נושא עדיין את הגרסה הקודמת.
  // ⚠️ ה-CSS המקביל (.glow-ig-wall__grid.is-scroll-capped) הוסר גם הוא.
  // ללא שער-מחלקה בכוונה: גם אם המחלקה כבר ירדה אבל ה-custom property נשאר
  // (מצב-ביניים אפשרי רק מול אסט מקוּשׁ ישן), הניקוי חייב לרוץ במלואו.
  #releaseCap() {
    if (!this.grid) return;
    this.grid.classList.remove('is-scroll-capped');
    this.grid.style.removeProperty('--igw-cap-height');
    this.grid.removeAttribute('tabindex');
    this.grid.removeAttribute('role');
    this.grid.removeAttribute('aria-label');
    this.grid.scrollTop = 0;
  }

  /* ---------------- פוסטים חיים (גשר-DOM ל-Instafeed) ---------------- */

  // שכבת ה-capture כבר הותקנה ברמת המודול (ראה למעלה) — כאן רק קריאה
  // אידמפוטנטית לביטחון, למקרה שהאלמנט נוסף לדף אחרי טעינת הסקריפט.
  // ההזרקה עצמה נשארת עצלה עד שהקיר מתקרב לוויופורט: מדידת ממדי התמונות
  // מורידה אותן, ואין סיבה שתתחרה על רוחב-פס עם ה-LCP (סקשן 6 בהומפייג').
  #initFeedHook() {
    if (!this.liveEnabled || !this.liveTemplate) {
      this.#noteFeedState('off');
      this.#settle();
      return;
    }

    glowIgFeedCapture.install();
    this.#noteFeedState('pending');

    const start = () => this.#startFeed();

    // רשת ביטחון: אם ה-IntersectionObserver לא יורה לעולם (אלמנט בגודל אפס,
    // אב עם content-visibility, טאב רקע, דפדפן שמדווח שגוי) — מתחילים בכל
    // זאת, הרבה אחרי חלון ה-LCP. בלי זה 'pending' יכול להישאר לצמיתות, וזה
    // בדיוק המצב שנראה כמו כשל-פיד ואי אפשר להבדיל ממנו.
    this.#fallbackTimer = setTimeout(start, GLOW_IG_FALLBACK_START);

    if ('IntersectionObserver' in window) {
      this.#nearObserver = new IntersectionObserver(
        (entries) => {
          if (!entries.some((entry) => entry.isIntersecting)) return;
          this.#nearObserver?.disconnect();
          this.#nearObserver = null;
          start();
        },
        { rootMargin: `${GLOW_IG_NEAR_VIEWPORT} 0px` }
      );
      this.#nearObserver.observe(this);
      return;
    }

    if ('requestIdleCallback' in window) {
      requestIdleCallback(start, { timeout: 3000 });
    } else {
      setTimeout(start, 600);
    }
  }

  // רושם את מצב הפיד: תמיד כ-data-igw-feed-state על ה-host (אבחון מה-DOM
  // בלי לפתוח קונסול), ובכשל גם console.warn אחד עם הסיבה. נראוּת בלבד —
  // ההתנהגות זהה בכל המצבים: no-op שקט, הקיר נשאר על האריחים המצוירים.
  #noteFeedState(state, message) {
    this.setAttribute('data-igw-feed-state', state);
    if (message) console.warn(`[glow-ig-wall] ${message}`);
  }

  // 04.10.2026 · סימן "הפיד הוכרע" (הוזרק, או נכשל ונשארים על המצוירים).
  // בגריד 3×3 של המובייל בדף הבית, תמונות האריחים המצוירים לא מוצגות עד
  // הסימן הזה (האריחים עצמם נשארים כשלד קרם, בלי CLS), כדי שלא יירדו 9
  // תמונות שהפוסטים החיים דוחפים מיד אל מחוץ לגריד. ראו glow-instagram-wall.liquid.
  #settle() {
    this.setAttribute('data-igw-settled', '');
  }

  // ריצה שהתחילה בדור אחר (ניתוק/חיבור מחדש באמצע) חייבת לפרוש בשקט.
  #staleRun(generation) {
    return this.#destroyed || generation !== this.#generation;
  }

  async #startFeed() {
    if (this.#feedStarted || this.#destroyed) return;
    this.#feedStarted = true;
    const run = this.#generation;
    clearTimeout(this.#fallbackTimer);

    const source = document.getElementById(GLOW_IG_SOURCE_ID);
    if (!source) {
      this.#noteFeedState(
        'no-source',
        'Instafeed mount point (#insta-feed) is missing — wall stays on static tiles.'
      );
      this.#settle();
      return;
    }

    const { posts, state } = await this.#waitForFeed(source);
    if (this.#staleRun(run)) return;

    if (!posts || posts.length === 0) {
      const messages = {
        empty: null,
        'no-render': 'the Instafeed script never rendered — is the app embed enabled?',
      };
      const message =
        state in messages
          ? messages[state]
          : `feed unavailable (${state}) — wall stays on static tiles.`;
      this.#noteFeedState(state, message);
      this.#settle();
      return;
    }

    // 'pending' מכאן ואילך פירושו רק דבר אחד: הפוסטים עדיין לא הושגו.
    this.#noteFeedState(state);

    // 04.10.2026 · גריד 3×3 במובייל בדף הבית: רק 9 אריחים גלויים, ולכן מזריקים
    // רק את הפוסטים שייראו. בלי מדידה מוקדמת: התמונות של האריחים החיים הן
    // lazy, ולכן יורדות רק כשהן מתקרבות למסך.
    // הסדר הוא זה ש-Instafeed מחזירה (מהחדש לישן; פוסט מוצמד בה מופיע ראשון), בלי מיון משלנו
    // (עילי 04.10, סבב 4: "אני לא רוצה עוד קוד שנתחזק סתם").
    const cap = this.#mobileCap();
    const used = cap > 0 ? posts.slice(0, cap) : posts;

    this.#injectLive(used);
    this.#settle();
  }

  // data-igw-m9 מרונדר רק בדף הבית. התקרה חלה רק מתחת ל-768 בזמן הטעינה;
  // בדסקטופ ההתנהגות זהה לקודם (כל הפוסטים).
  #mobileCap() {
    const n = parseInt(this.dataset.igwM9 || '0', 10);
    if (!(n > 0) || typeof window.matchMedia !== 'function') return 0;
    return window.matchMedia('(max-width: 767.98px)').matches ? n : 0;
  }

  // ממתין לראשון מבין: (א) תשובת הפיד שנקלטה מה-fetch של הסקריפט שלהם,
  // (ב) אריחים שהם רינדרו למכולה המוסתרת, (ג) timeout.
  //
  // סדר הפעולות כאן הוא הלב של התיקון (2026-08-02, נתפס בפרודקשן):
  // **סורקים קודם את מה שכבר קיים, ורק אם ריק מתחילים להאזין.** הסקריפט
  // שלהם מרנדר כבר בטעינת הדף, בעוד הנתיב שלנו מופעל מאוחר יותר (IO עם
  // rootMargin) — ואז אין mutations עתידיים לתפוס, ו-observer לבדו ממתין
  // לנצח. אידמפוטנטי: אם הסריקה תפסה, לא נפתח observer בכלל.
  // ה-MutationObserver (רק כשנדרש) עובד בדיסציפלינת
  // disconnect → takeRecords → reconnect (הלקח מ-GlowCartCarousel). אין סיכון
  // ללולאה — אנחנו לעולם לא כותבים לתוך #insta-feed, ומנתקים לצמיתות ברגע
  // שיש תוצאה.
  #waitForFeed(source) {
    return new Promise((resolve) => {
      let settled = false;
      let captureError = null;

      const finish = (posts, state) => {
        if (settled) return;
        settled = true;
        this.#abortWait = null;
        clearTimeout(this.#feedTimer);
        this.#stopFeedObserver();
        resolve({ posts, state });
      };

      // ניתוק משחרר את ההמתנה במקום להשאיר promise תלוי לנצח; הקורא מזהה
      // שהדור התחלף ופורש בשקט.
      this.#abortWait = () => finish(null, 'disconnected');

      // עדיפות א' — תשובת ה-fetch שנקלטה. היחידה שמחזיקה permalink אמיתי,
      // ולכן היא נבדקת ראשונה. אם היא כבר הגיעה, subscribe מחזיר מיד.
      glowIgFeedCapture.subscribe((result) => {
        if (result.posts) {
          finish(result.posts, result.posts.length > 0 ? 'ok' : 'empty');
          return;
        }
        // כשל ברשת שלהם: עדיין נותנים ל-DOM/timeout צ׳אנס, אבל זוכרים את הסיבה.
        captureError = result.error || 'capture-error';
      });

      if (settled) return;

      // עדיפות ב' — סריקת המצב הקיים. אם יש כאן אריחים, ה-fetch שלהם כבר
      // חזר מזמן, כלומר ה-capture או תפס או שלא יתפוס לעולם — אין למה להמתין.
      const scan = () => {
        const posts = this.#domPosts(source);
        if (posts.length === 0) return false;
        finish(posts, 'ok-dom');
        return true;
      };

      if (scan()) return;

      // רק עכשיו, כשה-DOM באמת ריק, מאזינים לרינדור עתידי.
      this.#feedObserver = new MutationObserver(() => {
        if (settled || !this.#feedObserver) return;
        this.#feedObserver.disconnect();
        this.#feedObserver.takeRecords();
        if (scan()) return;
        this.#feedObserver.observe(source, { childList: true, subtree: true });
      });
      this.#feedObserver.observe(source, { childList: true, subtree: true });

      this.#feedTimer = setTimeout(() => {
        const posts = this.#domPosts(source);
        if (posts.length > 0) {
          finish(posts, 'ok-dom');
          return;
        }
        finish([], captureError || this.#idleState());
      }, GLOW_IG_SOURCE_TIMEOUT);
    });
  }

  // מבחין בין "הסקריפט שלהם בכלל לא רץ" (app embed כבוי / חסום) לבין
  // "רץ אבל לא רינדר" — שני תרחישים שונים לגמרי לאבחון.
  #idleState() {
    return window.InstafeedAPI ? 'no-render' : 'no-script';
  }

  #stopFeedObserver() {
    if (!this.#feedObserver) return;
    this.#feedObserver.disconnect();
    this.#feedObserver.takeRecords();
    this.#feedObserver = null;
  }

  // גיבוי B: קריאת האריחים שהם רינדרו. data-instafeed-thumbnail מחזיק את
  // אותה כתובת standard_resolution שה-API מחזיר. permalink פר-פוסט אינו
  // קיים ב-DOM בהגדרות הנוכחיות (openIg=3 → lightbox, לא <a href>), ולכן
  // הקישור נופל לפרופיל.
  #domPosts(source) {
    const username = window.InstafeedAPI?.settings?.instances?.[0]?.accountUsername || '';
    const profile = username
      ? `https://www.instagram.com/${encodeURIComponent(username)}/`
      : 'https://www.instagram.com/';

    const posts = [];
    const items = source.querySelectorAll('.instafeed-new-layout-item');

    for (const item of items) {
      const img = item.querySelector('img');
      const url =
        item.getAttribute('data-instafeed-thumbnail') ||
        img?.getAttribute('data-instafeed-src') ||
        '';
      if (!url) continue;

      posts.push({
        id: `dom-${posts.length}`,
        link: profile,
        alt_text: this.#cleanDomAlt(img?.getAttribute('alt') || ''),
        images: { standard_resolution: { url } },
      });
    }

    return posts;
  }

  // ה-alt שלהם הוא "Instagram post with the caption: …" באנגלית קשיחה.
  // מפשיטים את התחילית ומשאירים את הקפשן עצמו (עברית) לקורא המסך.
  #cleanDomAlt(raw) {
    return raw.replace(GLOW_IG_DOM_ALT_PREFIX, '').replace(/\.{3}$/, '').trim();
  }

  // ה-API של Instafeed לא מחזיר width/height. הגובה של האריח קבוע ב-CSS
  // (aspect-ratio), ו-object-fit: cover חותך, ולכן אין צורך לדעת את הממדים
  // מראש. כשהם כן קיימים בתשובה, הם נכנסים ל-<img> (ראו #geometryFor).

  #injectLive(posts) {
    const fragment = document.createDocumentFragment();
    let built = 0;

    for (const post of posts) {
      const tile = this.#buildLiveTile(post);
      if (!tile) continue;
      fragment.appendChild(tile);
      built += 1;
    }
    if (built === 0) return;
    this.setAttribute('data-igw-live-count', String(built));

    // ראש הרשימה = ימין-למעלה ב-RTL. מכניסים לפני האריח האמיתי הראשון
    // (לא לפני clone), כדי שסדר המקוריים יישאר: חיים → מצוירים.
    const firstOriginal = this.grid.querySelector(
      '[data-glow-loop-item]:not([data-glow-loop-clone])'
    );
    this.grid.insertBefore(fragment, firstOriginal);

    this.#applyVisibility();
    // הקרוסלה כבר אתחלה (defer, לפנינו) ותפסה סט-מקוריים ישן → רענון חובה,
    // אחרת הלופ המעגלי במובייל נשבר.
    this.querySelector('glow-loop-carousel')?.refresh?.();
    this.#initSeeMore();
  }

  // כלל 3: בדיוק initial_count גלויים בדסקטופ — N חיים ואז מצוירים להשלמה.
  // כלל 2: המצוירים העודפים מסומנים כחבויים בלבד, לעולם לא נמחקים.
  #applyVisibility() {
    if (this.classList.contains('is-expanded')) return;

    const tiles = this.#realTiles();
    tiles.forEach((tile, index) => {
      const hide = index >= this.initial;
      tile.classList.toggle('glow-ig-tile--hidden', hide);
      if (hide) {
        tile.setAttribute('data-igw-hidden', '');
      } else {
        tile.removeAttribute('data-igw-hidden');
      }
    });
  }

  // משכפל את תבנית ה-Liquid (source: 'live') ומזריק פנימה את המדיה —
  // כך המבנה זהה 1:1 ל-snippet ואין שכפול markup ידני ב-JS.
  #buildLiveTile(post) {
    const media = post?.images?.standard_resolution;
    const src = media?.url;
    if (!src) return null;

    const tile = this.liveTemplate.content.firstElementChild?.cloneNode(true);
    const link = tile?.querySelector('.glow-ig-tile__link');
    const primary = tile?.querySelector('.glow-ig-tile__img--primary');
    if (!tile || !link || !primary) return null;

    const alt = this.#altFor(post);
    const { span, ratio, width, height } = this.#geometryFor(post, media);

    tile.style.setProperty('--igw-span', String(span));
    tile.setAttribute('data-igw-live', '');
    link.setAttribute('href', post.link || 'https://www.instagram.com/');
    link.setAttribute('aria-label', alt);

    const frame = document.createElement('div');
    frame.className = 'img-ar img-ar--cover';
    frame.style.setProperty('--aspect-ratio', String(ratio));

    const img = document.createElement('img');
    img.className = 'glow-ig-tile__theme-img theme-img';
    img.src = src;
    img.alt = alt;
    // CWV: אריח חי הוא לעולם לא ה-LCP (הוא נכנס אחרי שהקיר התקרב לוויופורט),
    // ולכן lazy מוחלט — כמו כל אריח מצויר ב-snippet כשהקיר מתחת לקיפול.
    img.loading = 'lazy';
    img.fetchPriority = 'low';
    img.decoding = 'async';
    img.sizes = GLOW_IG_SIZES;
    if (width && height) {
      img.width = width;
      img.height = height;
    }

    frame.appendChild(img);
    primary.replaceChildren(frame);
    return tile;
  }

  // ה-alt הוא גם ה-aria-label של הלינק. הפיד לא מחזיר alt_text, ולכן
  // הגיבוי הוא הקפשן — אבל רק *השורה הראשונה* שלו (המשפט הפותח), בלי
  // גוש ההאשטגים והשורות הנוספות שהופכים את ההכרזה לקורא-מסך לרעש.
  #altFor(post) {
    const explicit = (post?.alt_text || '').trim();
    if (explicit) return this.#trimAlt(explicit);

    const caption = (post?.caption?.text || '').trim();
    const firstLine = caption
      .split('\n')
      .map((line) => line.trim())
      .find((line) => line && !line.startsWith('#'));

    const text = (firstLine || caption).replace(/\s+/g, ' ').trim();
    if (text) return this.#trimAlt(text);

    return GLOW_IG_ALT_FALLBACK;
  }

  #trimAlt(text) {
    const clean = text.replace(/\s+/g, ' ').trim();
    return clean.length > 120 ? `${clean.slice(0, 117)}...` : clean;
  }

  // אותה לוגיקת span כמו ב-Liquid: ar<=0.62 → 5, ar<=0.8 → 4, אחרת 3.
  // לוידאו משתמשים ב-thumbnail (images.standard_resolution), לא ב-videos.
  #geometryFor(post, media) {
    const width = Number(media?.width) || Number(post?.width) || 0;
    const height = Number(media?.height) || Number(post?.height) || 0;
    if (!width || !height) return { span: 3, ratio: 0.75, width: 0, height: 0 };

    const ratio = width / height;
    let span = 3;
    if (ratio <= 0.62) {
      span = 5;
    } else if (ratio <= 0.8) {
      span = 4;
    }
    return { span, ratio, width, height };
  }
}

if (!customElements.get('glow-instagram-wall')) {
  customElements.define('glow-instagram-wall', GlowInstagramWall);
}
